/**
 * Hook to fetch motrQ questionnaires that should appear as dashboard tabs.
 * Filters questionnaires by `showOnDashboard === true`.
 */

import { useMemo } from 'react';
import { useSettings } from '@/api/hooks/useSettings';
import { MOTRQ_SETTINGS, getMotrQSettingKeys } from '@/features/settings/constants';
import type { Questionnaire } from '@/features/settings/types';

interface DashboardQuestionnairesResult {
  questionnaires: Questionnaire[];
  baseUrl: string;
  isLoading: boolean;
}

export const useMotrQDashboardQuestionnaires = (enabled = true): DashboardQuestionnairesResult => {
  const { data: settings, isLoading } = useSettings(getMotrQSettingKeys(), enabled);

  const result = useMemo(() => {
    if (!settings || settings.length === 0) {
      return { questionnaires: [], baseUrl: '' };
    }

    const baseUrlSetting = settings.find((s) => s.name === MOTRQ_SETTINGS.BASE_URL);
    const baseUrl = baseUrlSetting?.value || '';

    if (!baseUrl) {
      return { questionnaires: [], baseUrl: '' };
    }

    const questionnairesSetting = settings.find((s) => s.name === MOTRQ_SETTINGS.QUESTIONNAIRES);
    let questionnaires: Questionnaire[] = [];
    if (questionnairesSetting?.value) {
      try {
        const all: Questionnaire[] = JSON.parse(questionnairesSetting.value);
        questionnaires = all.filter((q) => q.showOnDashboard === true);
      } catch {
        questionnaires = [];
      }
    }

    return { questionnaires, baseUrl };
  }, [settings]);

  return { ...result, isLoading };
};
